import classNames from 'classnames';
import { Dayjs } from 'dayjs';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { MouseEvent } from 'react';
import { useOnClickOutside } from '../hooks';
import { CalendarIcon, CancelIcon } from '../icons';
import { CalendarInput } from './CalendarInput';
import styles from './FilterItem.module.scss';
import { Icon } from './Icon';
import { Select, SelectOption } from './Select';

interface SelectFilterProps<T> {
  options: SelectOption<T>[];
  value?: T;
  placeholder?: string;
  onChange: (value: T | null) => void;
  isClearable?: boolean;
}

const SelectFilter = <T,>({
  options,
  value,
  placeholder,
  onChange,
  isClearable = true,
}: SelectFilterProps<T>) => {
  return (
    <Select
      isClearable={isClearable}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={styles.selectFilter}
      label=""
    />
  );
};

interface DateFilterProps {
  placeholder?: string;
  showIcon?: boolean;
  onChange: (value: [Dayjs, Dayjs] | null) => void;
  value?: [Dayjs, Dayjs];
}

const DateFilter = ({ onChange, value, placeholder, showIcon = true }: DateFilterProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (dates?.[0] !== null && dates?.[1] !== null) {
        onChange(dates as [Dayjs, Dayjs]);
      } else {
        onChange(null);
      }
      setOpen(false);
    },
    [onChange],
  );

  const handleClick = useCallback(() => {
    setOpen(open => !open);
  }, []);

  const handleClear = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      onChange(null);
      e.stopPropagation();
    },
    [onChange],
  );

  useOnClickOutside({
    ref: containerRef,
    handler: () => setOpen(false),
    ignoreList: ['.ant-picker-panel-layout'],
  });

  return (
    <div
      ref={containerRef}
      className={classNames('h-full cursor-pointer', {
        'min-w-40': !value,
        'min-w-56': !!value,
      })}
    >
      <div className="[&>*]:h-0">
        <CalendarInput
          range
          open={open}
          rangeOnChange={handleChange}
          rangeValue={value}
          format="DD-MM-YYYY"
          style={{ display: 'block', opacity: 0, width: 0, height: 0 }}
        />
      </div>

      <div
        className={classNames('flex gap-3  items-center h-full text-sm text-gray-900', {
          'justify-center': !!value,
        })}
        onClick={handleClick}
      >
        {showIcon && <img src={CalendarIcon} alt="calendar" width={20} height={20} />}
        {value ? (
          <>
            <span className="text-nowrap">{value[0].format('DD-MM-YYYY')}</span>
            <span>-</span>
            <span className="text-nowrap">{value[1].format('DD-MM-YYYY')}</span>
            <button onClick={handleClear}>
              <Icon Source={CancelIcon} color="gray" />
            </button>
          </>
        ) : (
          <div className="text-sm text-neutral-400">{placeholder || 'Any date'}</div>
        )}
      </div>
    </div>
  );
};

interface BaseFilterItemProps {
  className?: string;
  classes?: {
    label?: string;
    element?: string;
  };
  label?: string;
}

interface SelectFilterItemProps<T> extends BaseFilterItemProps {
  type: 'select';
  placeholder?: string;
  value?: T;
  options: SelectOption<T>[];
  onChange: (value: T | null) => void;
  isClearable?: boolean;
}

interface DateFilterItemProps extends BaseFilterItemProps {
  type: 'date';
  showIcon?: boolean;
  placeholder?: string;
  value?: [Dayjs, Dayjs];
  onChange: (value: [Dayjs, Dayjs] | null) => void;
}

interface CustomFilterItemProps extends BaseFilterItemProps {
  type: 'custom';
  children: ReactNode;
}

type FilterItemProps<T> = SelectFilterItemProps<T> | DateFilterItemProps | CustomFilterItemProps;

export function FilterItem<T>(props: FilterItemProps<T>) {
  const { label, type, className, classes } = props;
  return (
    <div
      className={classNames(
        'flex items-center border',
        { rounded: !className?.includes('rounded') },
        className,
      )}
    >
      {label && (
        <div
          className={classNames('text-sm text-gray-600 px-3 py-1.5 text-nowrap', classes?.label)}
        >
          {label}
        </div>
      )}
      <div
        className={classNames(
          'h-full px-3',
          {
            'border-l': label,
            'py-2': type === 'date' && !classes?.element?.includes('py-'),
            'py-1.5': type !== 'date' && !classes?.element?.includes('py-'),
          },
          classes?.element,
        )}
      >
        {type === 'select' && (
          <SelectFilter
            placeholder={props.placeholder}
            options={props.options}
            value={props.value}
            onChange={props.onChange}
            isClearable={props.isClearable}
          />
        )}
        {type === 'date' && (
          <DateFilter
            placeholder={props.placeholder}
            showIcon={props.showIcon}
            value={props.value}
            onChange={props.onChange}
          />
        )}
        {type === 'custom' && props.children}
      </div>
    </div>
  );
}
